import { useQuery } from '@apollo/client';
import {
    DSIconButton,
    DSLoadMoreButton,
    DSTypography,
    DSWell,
    DSWellHeader,
    DSWellHeaderRightContent,
    DSWellHeaderTitle,
} from '@deltasierra/components';
import RefreshIcon from '@material-ui/icons/Refresh';
import React from 'react';
import styled from 'styled-components';
import { useConnectionFetchMore } from '../../../../../graphql/hooks';
import { relayConnectionToArray } from '../../../../../graphql/utils';
import { useApproveBlockedPreview } from '../hooks/useApproveBlockedPreview';
import { BlockedPreviewsTable } from './BlockedPreviewsTable';
import { GET_BLOCKED_PREVIEWS_QUERY } from './BlockedPreviewsWell.queries';
import {
    GetBlockedTemplateContentBuildEmailPreviews,
    GetBlockedTemplateContentBuildEmailPreviewsVariables,
} from './__graphqlTypes/GetBlockedTemplateContentBuildEmailPreviews';

const StyledCenter = styled.div`
    text-align: center;
`;

export function BlockedPreviewsWell(): JSX.Element {
    const { data, fetchMore, loading, refetch } = useQuery<
        GetBlockedTemplateContentBuildEmailPreviews,
        GetBlockedTemplateContentBuildEmailPreviewsVariables
    >(GET_BLOCKED_PREVIEWS_QUERY, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: {
            first: 10,
        },
    });
    const blockedPreviews = React.useMemo(
        () => relayConnectionToArray(data?.admin.blockedTemplateContentBuildEmailPreviews),
        [data?.admin.blockedTemplateContentBuildEmailPreviews],
    );
    const [handleFetchMore, hasNextPage] = useConnectionFetchMore(
        data?.admin.blockedTemplateContentBuildEmailPreviews,
        async after => fetchMore({ variables: { after, first: 20 } }),
    );

    const { mutationLoading, onApprove } = useApproveBlockedPreview();

    return (
        <DSWell>
            <DSWellHeader>
                <DSWellHeaderTitle>{'Blocked template previews'}</DSWellHeaderTitle>
                <DSWellHeaderRightContent>
                    <DSIconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </DSIconButton>
                </DSWellHeaderRightContent>
            </DSWellHeader>
            <DSTypography gutterBottom>
                {'These are the email previews that require approval before being sent to the recipient.'}
            </DSTypography>
            <BlockedPreviewsTable
                disableActions={loading}
                loading={loading || mutationLoading}
                rows={blockedPreviews}
                onApprove={onApprove}
            />
            {hasNextPage && (
                <StyledCenter>
                    <DSLoadMoreButton gutterTop loading={loading} onClick={handleFetchMore} />
                </StyledCenter>
            )}
        </DSWell>
    );
}
BlockedPreviewsWell.displayName = 'BlockedPreviewsWell';
